import { ColDef, ColumnState, ValueFormatterParams } from "ag-grid-community";
import { MultiSelectOption } from "@/lib/shadcn/components/multi-select";
import { GreekDateComponent } from "@/lib/grid/greek-date-component";

/**
 * Timezone-independent comparator for `agDateColumnFilter` columns whose cell
 * values are date-only `Date` objects parsed at **UTC** midnight
 * (`new Date("2026-06-04")` === `2026-06-04T00:00:00Z`).
 *
 * AG Grid's date filter reads the cell `Date` in **local** time and passes the
 * picked filter value as `filterLocalDateAtMidnight` (a `Date` at *local*
 * midnight). In a UTC-negative timezone a UTC-midnight cell date reads as the
 * PREVIOUS calendar day locally (e.g. America/New_York: `getDate()` on
 * `2026-06-04T00:00:00Z` is 3), so AG Grid's default comparator drops rows that
 * the user clearly intended to match — the "No Matching Rows" bug. In UTC the
 * shift never happens, which is why it passes in CI but breaks in a UTC- prod.
 *
 * The fix: compare the cell's **UTC** date-parts (the intended stored date)
 * against the filter's **local** date-parts (what AG Grid already gave us at
 * local midnight). Both sides are reduced to a comparable yyyymmdd key so the
 * comparison is purely calendar-date, with zero timezone influence.
 *
 * Wire it via `filterParams: { comparator: utcDateFilterComparator }` on every
 * `agDateColumnFilter` column backed by a UTC-midnight `Date`.
 */
const utcDateFilterComparator = (filterLocalMidnight: Date, cellValue: unknown): number => {
    if (cellValue == null) return -1;
    const c = new Date(cellValue as string | number | Date);
    if (Number.isNaN(c.getTime())) return -1;
    const cellKey = c.getUTCFullYear() * 10000 + c.getUTCMonth() * 100 + c.getUTCDate();
    const filterKey =
        filterLocalMidnight.getFullYear() * 10000 +
        filterLocalMidnight.getMonth() * 100 +
        filterLocalMidnight.getDate();
    return cellKey < filterKey ? -1 : cellKey > filterKey ? 1 : 0;
};

/**
 * Shared `filterParams` for UTC-midnight date columns. Spread onto any
 * `agDateColumnFilter` column: `filterParams: utcDateFilterParams`.
 */
const utcDateFilterParams = { comparator: utcDateFilterComparator } as const;

/**
 * Shared `agDateColumnFilter` config for UTC-midnight date columns. Spread onto a
 * `ColDef` (`...greekDateColumnFilter`) to get, in one place:
 *
 * - `filter: "agDateColumnFilter"` — the date filter;
 * - `filterParams: utcDateFilterParams` — the TZ-independent matching comparator;
 * - `dateComponent: GreekDateComponent` — a custom Date Component (AG Grid v35
 *   reads it from `colDef.dateComponent`) so BOTH the filter popup body and the
 *   floating-filter row render and parse `dd/mm/yyyy` instead of the browser's
 *   locale-dependent native `<input type="date">`.
 *
 * Keep all four reservation date columns on this so they stay in sync (DRY).
 */
const greekDateColumnFilter = {
    filter: "agDateColumnFilter",
    filterParams: utcDateFilterParams,
    dateComponent: GreekDateComponent,
} as const satisfies Partial<ColDef>;

/**
 * Shared `agSetColumnFilter` config for a column whose CELL shows something
 * other than its raw value — a badge `cellRenderer`, a boolean checkbox, a
 * prettified wire value.
 *
 * The Set Filter builds its checkbox list from the column's raw cell VALUES and
 * formats each one with `filterParams.valueFormatter`, falling back to
 * `colDef.valueFormatter`. It NEVER consults `cellRenderer`. So a column whose
 * badge renderer shows "Active"/"Cancelled" still offered `true`/`false` in its
 * own filter — the column and its filter disagreed, which is what this closes.
 *
 * Pass the mapping the cell itself uses:
 *   ...labelledSetFilter((v) => (v === "hosthub" ? "HostHub" : "Manual"))
 *
 * NOTE: a `valueFormatter` on the ColDef would also reach the Set Filter, but it
 * would additionally override the cell's own rendering — which is why the label
 * mapping is attached to `filterParams` and the `cellRenderer` is left alone.
 */
const labelledSetFilter = (format: (value: unknown) => string) =>
    ({
        filter: "agSetColumnFilter",
        filterParams: {
            valueFormatter: (params: ValueFormatterParams): string => format(params.value),
        },
    }) as const satisfies Partial<ColDef>;

/**
 * `labelledSetFilter` for the common boolean case: spread onto a boolean column
 * and pass the two labels its cell already shows.
 *
 *   ...booleanSetFilter("Cancelled", "Active")   // isCancelled
 *   ...booleanSetFilter("Yes", "No")             // STR Done / ERP Done / Admin
 *
 * Anything that isn't truthy maps to `falseLabel`, matching the `?? false`
 * default every boolean cell renderer applies to a missing value — so a blank
 * row is offered under the same label the grid renders for it, rather than as a
 * separate `(Blanks)` entry that the column never displays.
 *
 * The string `"true"`/`"false"` cases are load-bearing: AG Grid stringifies
 * values when keying the Set Filter list, so the formatter must not assume it
 * receives a real boolean.
 */
const booleanSetFilter = (trueLabel: string, falseLabel: string) =>
    labelledSetFilter((value) => (value === true || value === "true" ? trueLabel : falseLabel));

const getColumnVisibilityOptions = (colDefs: ColDef[]): MultiSelectOption[] => {
    return colDefs
        .filter((col) => col.field && col.headerName) // Only include columns with both field and header
        .map((colDef) => ({
            label: colDef.headerName!,
            value: colDef.field!,
        }));
};

// Helper function to get initially visible columns
const getVisibleColumns = (colDefs: ColDef[], columnState?: ColumnState[]): string[] => {
    return colDefs
        .filter((col) => {
            const columnStateEntry = columnState?.find((state) => state.colId === col.field);
            // If column state exists, use its hide value, otherwise check colDef.hide
            const isHidden = columnStateEntry ? columnStateEntry.hide : col.hide;
            return col.field && !isHidden;
        })
        .map((col) => col.field!);
};

export {
    booleanSetFilter,
    getColumnVisibilityOptions,
    getVisibleColumns,
    greekDateColumnFilter,
    labelledSetFilter,
    utcDateFilterComparator,
    utcDateFilterParams,
};
